import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useApi } from "../hooks/useApi";

/**
 * Asset playbook, phase 1 (§11h): six structured questions plus freeform additional info,
 * versioned append-only. Risk managers edit; everyone reads. The save contract is optimistic
 * concurrency — a 409 renders the newer version beside the user's edits for an explicit
 * load-or-overwrite choice, because a collision must never silently promote a stale form.
 *
 * Phase 2: a risk manager can assign someone to fill the playbook out. The server's canWrite
 * covers the assignee-write grant (an open assignment on this asset), which the frontend's
 * role mirror cannot see — so write visibility trusts the response, not only canManage.
 */
/** Chip-list input with observed-vocabulary suggestions (datalist) and optional validation. */
function ChipListInput({ label, values, onChange, suggestions, placeholder, validate, transform, hint, invalidMessage }) {
  const [draft, setDraft] = useState("");
  const [invalid, setInvalid] = useState(false);
  const listId = `chips-${label.replace(/\W+/g, "-")}`;
  const add = () => {
    const value = (transform || ((v) => v))(draft.trim());
    if (!value) return;
    if (validate && !validate(value)) {
      // Refusing without saying why is finding-6's complaint — the message names the rule.
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (!values.some((v) => v.toLowerCase() === value.toLowerCase())) onChange([...values, value]);
    setDraft("");
  };
  return (
    <div className="mb-1.5">
      <label className="block text-[11px] font-medium text-content-primary mb-0.5">{label}</label>
      <div className="flex flex-wrap gap-1 mb-1">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-theme-hover border border-border text-[11px] text-content-primary">
            {v}
            <button onClick={() => onChange(values.filter((x) => x !== v))} className="text-content-secondary hover:text-red-700" aria-label={`Remove ${v}`}>×</button>
          </span>
        ))}
      </div>
      <input
        list={listId}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={placeholder}
        className="w-full text-xs border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary"
      />
      <datalist id={listId}>
        {(suggestions || []).map((sug) => <option key={sug} value={sug} />)}
      </datalist>
      {invalid && (
        <div className="text-[10px] text-red-700 mt-0.5">
          {invalidMessage || "That value is not accepted here."}
        </div>
      )}
      {hint && <div className="text-[10px] text-content-secondary mt-0.5">{hint}</div>}
    </div>
  );
}

export default function AiPlaybookPanel({ assetId, data, canManage, onSaved }) {
  const apiCall = useApi();
  const [editing, setEditing] = useState(false);
  const [answers, setAnswers] = useState({});
  const [additionalInfo, setAdditionalInfo] = useState("");
  // §11l declared expected behavior — three independent lists; empty means "no declaration".
  const [expectedModels, setExpectedModels] = useState([]);
  const [expectedResources, setExpectedResources] = useState([]);
  const [expectedCountries, setExpectedCountries] = useState([]);
  const [baseVersion, setBaseVersion] = useState(0);
  const [conflict, setConflict] = useState(null); // the newer version from a 409
  const [saveError, setSaveError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null); // a fetched historical version
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState(null);

  if (!data) return null;
  const { questions, current, history, assignment } = data;
  const canWrite = data.canWrite ?? canManage;
  const canAssign = data.canAssign ?? canManage;
  const openAssignment = assignment && !assignment.completedAt ? assignment : null;

  const openAssignPicker = async () => {
    setAssigning(true);
    setAssignError(null);
    setSelectedAssignee(openAssignment ? String(openAssignment.assigneeUserId) : "");
    if (!assignableUsers) {
      try {
        const res = await apiCall("GET", "/assessments/assignableUsers");
        setAssignableUsers(Array.isArray(res) ? res : []);
      } catch {
        setAssignableUsers([]);
        setAssignError("The assignable users could not be loaded — try again.");
      }
    }
  };

  const saveAssignment = async (assigneeUserId) => {
    setAssignBusy(true);
    setAssignError(null);
    try {
      await apiCall("PUT", `/ai-assets/assets/${assetId}/playbook/assignment`, { assigneeUserId });
      setAssigning(false);
      onSaved?.();
    } catch (e) {
      setAssignError(e?.response?.data?.message || "The assignment could not be saved.");
    } finally {
      setAssignBusy(false);
    }
  };

  const beginEdit = (from) => {
    const source = from ?? current;
    const filled = {};
    (source?.answers || []).forEach((a) => { filled[a.id] = a.answer; });
    setAnswers(filled);
    setAdditionalInfo(source?.additionalInfo || "");
    setExpectedModels(source?.declaredBehavior?.models || []);
    setExpectedResources(source?.declaredBehavior?.resources || []);
    setExpectedCountries(source?.declaredBehavior?.originCountries || []);
    // The base is the version being edited FROM. Loading a conflict's newer version must carry
    // that version as the base — resetting to the stale prop re-conflicted every save after
    // "Load newer version".
    setBaseVersion(source?.version ?? 0);
    setConflict(null);
    setSaveError(null);
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    setSaveError(null);
    try {
      await apiCall("PUT", `/ai-assets/assets/${assetId}/playbook`, {
        baseVersion,
        answers,
        additionalInfo,
        // Always sent as one presence-tracked object: the server preserves the declaration
        // when the property is ABSENT (older clients) and clears it when supplied empty —
        // this form is a current client, so it always states its whole declaration.
        declaredBehavior: {
          models: expectedModels,
          resources: expectedResources,
          originCountries: expectedCountries,
        },
      });
      setEditing(false);
      setConflict(null);
      onSaved?.();
    } catch (e) {
      if (e?.response?.status === 409) {
        // Someone else saved first. Show their version; the user chooses explicitly.
        setConflict(e.response.data?.current || null);
        setBaseVersion(e.response.data?.current?.version ?? 0);
      } else {
        setSaveError(e?.response?.data?.message || "The playbook could not be saved.");
      }
    } finally {
      setBusy(false);
    }
  };

  // The current version renders the catalog merged with its answers (unanswered questions show
  // as prompts-without-answers), PLUS any answered question whose id has since left the catalog —
  // the snapshots exist precisely so retirement cannot erase what was written. Historical
  // versions render their own snapshotted answer list directly.
  const readOnlyVersion = (version, label, { historical = false } = {}) => {
    const saved = version?.answers || [];
    const rows = historical
      ? saved
      : [
          ...questions.map((q) => {
            const snapshotted = saved.find((a) => a.id === q.id);
            return { id: q.id, prompt: snapshotted?.prompt || q.prompt, answer: snapshotted?.answer };
          }),
          ...saved.filter((a) => !questions.some((q) => q.id === a.id)),
        ];
    return (
      <div className="space-y-2">
        {label && <div className="text-xs font-semibold text-content-primary">{label}</div>}
        {rows.map((row) => (
          <div key={row.id}>
            <div className="text-xs font-medium text-content-primary">{row.prompt}</div>
            <div className="text-xs text-content-secondary whitespace-pre-wrap">
              {row.answer || "Not filled in."}
            </div>
          </div>
        ))}
        {version?.additionalInfo && (
          <div>
            <div className="text-xs font-medium text-content-primary">Additional information</div>
            <div className="text-xs text-content-secondary whitespace-pre-wrap">{version.additionalInfo}</div>
          </div>
        )}
        {version?.declaredBehavior && (
          <div>
            <div className="text-xs font-medium text-content-primary">Expected behavior</div>
            <div className="text-xs text-content-secondary">
              {version.declaredBehavior.models && (
                <div>Models: {version.declaredBehavior.models.join(", ")}</div>
              )}
              {version.declaredBehavior.resources && (
                <div>Resources: {version.declaredBehavior.resources.join(", ")}</div>
              )}
              {version.declaredBehavior.originCountries && (
                <div>Authentication origins: {version.declaredBehavior.originCountries.join(", ")}</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Viewing a historical version replaces the pane until dismissed.
  if (viewingVersion) {
    return (
      <div className="space-y-3">
        <button
          onClick={() => setViewingVersion(null)}
          className="text-xs text-blue-600 hover:underline"
        >
          ← Back to the current playbook
        </button>
        {readOnlyVersion(viewingVersion,
          `Version ${viewingVersion.version} — ${viewingVersion.author || "unknown"}, ${String(viewingVersion.createdAt).slice(0, 10)}`,
          { historical: true })}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {openAssignment && (
        <div className="text-[11px] text-content-secondary bg-theme-hover border border-border rounded-md px-2.5 py-1.5">
          Assigned to <b className="text-content-primary">{openAssignment.assigneeUserName || "a user"}</b>
          {openAssignment.assignedByUserName ? ` by ${openAssignment.assignedByUserName}` : ""}
          {openAssignment.assignedAt ? `, ${String(openAssignment.assignedAt).slice(0, 10)}` : ""} —
          the task stays in their My Tasks until the playbook is saved.
        </div>
      )}

      {assigning && (
        <div className="text-xs border border-border rounded-md p-2.5 space-y-2">
          <div className="font-medium text-content-primary">
            {openAssignment ? "Reassign the playbook" : "Assign someone to fill this playbook out"}
          </div>
          {assignableUsers === null ? (
            <div className="text-content-secondary">Loading users…</div>
          ) : (
            <select
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              className="w-full text-xs border border-border rounded-lg px-2 py-1.5 bg-theme-primary text-content-primary"
            >
              <option value="">Select a user…</option>
              {assignableUsers.map((u) => (
                <option key={u.id} value={u.id}>{u.fullName || u.email}</option>
              ))}
            </select>
          )}
          <div className="text-[11px] text-content-secondary">
            The assignee is emailed and gets a My Tasks entry, and can edit this asset's playbook
            (only this one) until it is saved.
          </div>
          {assignError && <div className="text-red-700">{assignError}</div>}
          <div className="flex gap-2">
            <button
              onClick={() => saveAssignment(parseInt(selectedAssignee))}
              disabled={assignBusy || !selectedAssignee}
              className="px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {assignBusy ? "Saving…" : "Assign"}
            </button>
            {openAssignment && (
              <button
                onClick={() => saveAssignment(null)}
                disabled={assignBusy}
                className="px-2 py-1 rounded-md border border-border text-content-secondary hover:bg-theme-hover"
              >
                Withdraw assignment
              </button>
            )}
            <button
              onClick={() => { setAssigning(false); setAssignError(null); }}
              className="px-2 py-1 rounded-md border border-border text-content-secondary hover:bg-theme-hover"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!editing && (
        <>
          {current ? (
            readOnlyVersion(current,
              `Current (v${current.version} — ${current.author || "unknown"}, ${String(current.createdAt).slice(0, 10)})`)
          ) : (
            <div className="text-xs text-content-secondary">
              No playbook yet. It answers the questions a containment decision needs — who to
              tell first, what breaks, when to act and when to wait — and appears read-only on
              the response request form at the moment of decision.
            </div>
          )}
          <div className="flex gap-2">
            {canWrite && (
              <button
                onClick={() => beginEdit()}
                className="text-xs px-3 py-1.5 rounded-md border border-border bg-theme-hover text-content-primary hover:bg-theme-secondary"
              >
                {current ? "Edit playbook" : "Create playbook"}
              </button>
            )}
            {canAssign && !assigning && (
              <button
                onClick={openAssignPicker}
                className="text-xs px-3 py-1.5 rounded-md border border-border text-content-secondary hover:bg-theme-hover"
              >
                {openAssignment ? "Reassign…" : "Assign…"}
              </button>
            )}
          </div>
        </>
      )}

      {editing && (
        <div className="space-y-3">
          {conflict && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2.5 space-y-2">
              <div className="flex items-start gap-1.5 text-amber-900">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  <b>The playbook changed while you were editing</b> — version {conflict.version}
                  {conflict.author ? ` by ${conflict.author}` : ""} was saved. Nothing is lost:
                  your edits are still below. Load their version, or save again to overwrite it
                  with yours.
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => beginEdit(conflict)}
                  className="px-2 py-1 rounded border border-amber-300 text-amber-900 hover:bg-amber-100"
                >
                  Load version {conflict.version}
                </button>
                <button
                  onClick={save}
                  disabled={busy}
                  className="px-2 py-1 rounded border border-amber-300 text-amber-900 hover:bg-amber-100"
                >
                  Overwrite with my edits
                </button>
              </div>
            </div>
          )}

          {questions.map((q) => (
            <div key={q.id}>
              <label className="block text-xs font-medium text-content-primary mb-0.5">{q.prompt}</label>
              <textarea
                rows={2}
                value={answers[q.id] || ""}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                className="w-full text-xs border border-border rounded-lg px-2 py-1.5 bg-theme-primary text-content-primary"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium text-content-primary mb-0.5">
              Additional information
            </label>
            <textarea
              rows={3}
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              className="w-full text-xs border border-border rounded-lg px-2 py-1.5 bg-theme-primary text-content-primary"
            />
          </div>

          {/* §11l expected behavior. Suggestions come from what telemetry has actually observed —
              declaring should mean picking from reality first, typing second. Leaving a list
              empty makes NO claim; a non-empty list is an affirmative expected set that
              Behavior 360 confronts with post-declaration observations. */}
          <div className="pt-2 border-t border-border">
            <div className="text-xs font-medium text-content-primary mb-0.5">
              Expected behavior (optional)
            </div>
            <div className="text-[11px] text-content-secondary mb-1.5">
              What this asset is supposed to use. Behavior 360 compares observations made after
              the declaration against these — informational only, nothing is blocked or flagged
              as a risk. An empty list declares nothing.
            </div>
            <ChipListInput
              label="Expected models"
              values={expectedModels}
              onChange={setExpectedModels}
              suggestions={data.vocabulary?.models}
              placeholder="e.g. claude-fable-5 — covers versioned ids like claude-fable-5-2026…"
            />
            <ChipListInput
              label="Expected resources"
              values={expectedResources}
              onChange={setExpectedResources}
              suggestions={data.vocabulary?.resources}
              placeholder="resource identifiers as sign-in telemetry records them"
            />
            <ChipListInput
              label="Expected authentication origins (2-letter country codes)"
              values={expectedCountries}
              onChange={setExpectedCountries}
              suggestions={data.vocabulary?.originCountries}
              placeholder="e.g. US"
              validate={(v) => /^[A-Za-z]{2}$/.test(v)}
              transform={(v) => v.toUpperCase()}
              invalidMessage={"Two-letter ISO country code required (e.g. US, DE) - names are not accepted."}
              hint="For hosted services this can describe the provider's infrastructure, not a person."
            />
          </div>
          <div className="text-[11px] text-content-secondary">
            Every question is optional, but an entirely empty playbook cannot be saved. Do not
            store credentials, secrets, or recovery tokens here — this is operational
            documentation, not a vault. Saving creates a new version; earlier versions stay in
            the history below.
          </div>
          {saveError && <div className="text-xs text-red-700">{saveError}</div>}
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={busy}
              className="text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save (creates a new version)"}
            </button>
            <button
              onClick={() => { setEditing(false); setConflict(null); }}
              className="text-xs px-3 py-1.5 rounded-md border border-border text-content-secondary hover:bg-theme-hover"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {history?.length > 0 && (
        <div className="pt-2 border-t border-border">
          <div className="text-[11px] font-semibold text-content-secondary uppercase mb-1">
            Version history
          </div>
          <div className="space-y-0.5 text-[11px] text-content-secondary">
            {history.map((v) => (
              <div key={v.version}>
                v{v.version} — {v.author || "unknown"}, {String(v.createdAt).slice(0, 10)}{" "}
                <button
                  disabled={historyBusy}
                  onClick={async () => {
                    setHistoryBusy(true);
                    setHistoryError(null);
                    try {
                      const full = await apiCall(
                        "GET", `/ai-assets/assets/${assetId}/playbook/${v.version}`);
                      setViewingVersion(full);
                    } catch {
                      setHistoryError(`Version ${v.version} could not be loaded — try again.`);
                    } finally {
                      setHistoryBusy(false);
                    }
                  }}
                  className="text-blue-600 hover:underline disabled:opacity-60"
                >
                  {historyBusy ? "loading…" : "view"}
                </button>
              </div>
            ))}
            {historyError && <div className="text-red-700">{historyError}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
